import React, { useState, useEffect, useRef } from 'react';

import Swal from 'sweetalert2';

import { usePrevious } from 'hooks';
import { Covid19Settings } from '@bb/domain/dist/types';
import COVID19Settings from './COVID19Settings';
import COVID19TemplateModal from './COVID19TemplateModal';
import COVID19AckTemplateContainer from './COVID19AckTemplateContainer';

export type COVID19SettingsContainerProps = {
  type?: 'org' | 'listing';
  getCovid19OrgSettings: () => Promise<Covid19Settings>;
  saveCovid19OrgSettings: (
    settings: Covid19Settings,
  ) => Promise<Covid19Settings>;
};

type storedTemplateType = {
  popupTemplate?: string;
  ackTemplate?: string;
};

const COVID19SettingsContainer: React.FC<COVID19SettingsContainerProps> = ({
  getCovid19OrgSettings,
  saveCovid19OrgSettings,
  type,
}) => {
  const [covid19Settings, setCovid19Settings] = useState({
    mlsBookingPopup: false,
    newBookingAck: false,
    popupTemplate: '',
    ackTemplate: '',
    covid19PrecautionsBtn: false,
  });

  const [showAckTemplateModal, setShowAckTemplateModal] = useState({
    show: false,
    preview: false,
  });
  const [showPopupTemplateModal, setShowPopupTemplateModal] = useState({
    show: false,
    preview: false,
  });
  const [shouldSubmit, setShouldSubmit] = useState(false);

  const initialTemplates = useRef<storedTemplateType>(null);

  const storeTemplateForRestore = ({
    popupTemplate,
    ackTemplate,
  }: storedTemplateType) => {
    initialTemplates.current = {
      popupTemplate,
      ackTemplate,
    };
  };

  const restoreTemplate = (template: 'popupTemplate' | 'ackTemplate') => {
    if (!initialTemplates.current) return;
    setCovid19Settings({
      ...covid19Settings,
      [template]: initialTemplates.current[template],
    });
  };

  useEffect(() => {
    (async () => {
      const orgSettings = await getCovid19OrgSettings();
      setCovid19Settings(orgSettings);
      storeTemplateForRestore(orgSettings);
    })();
  }, []);

  // To get previous state value and see if its been changed from false to true
  const prevNewBookingAck = usePrevious(covid19Settings.newBookingAck);
  const prevMlsBookingPopup = usePrevious(covid19Settings.mlsBookingPopup);
  const prevCovid19PrecautionsBtn = usePrevious(
    covid19Settings.covid19PrecautionsBtn,
  );

  const checkToggleModals = async () => {
    if (
      type === 'org' &&
      prevMlsBookingPopup === false &&
      covid19Settings.mlsBookingPopup
    ) {
      const { value } = await Swal.fire({
        title: 'MLS COVID-19 WARNING',
        showCancelButton: true,
        showConfirmButton: true,
        reverseButtons: true,
        text: 'Are you sure you want to implement a COVID-19 warning modal when a showing agent books through the MLS?',
        type: 'info',
      });
      if (!value) {
        setCovid19Settings({ ...covid19Settings, mlsBookingPopup: false });
        return false;
      }
    }

    if (
      type === 'org' &&
      prevNewBookingAck === false &&
      covid19Settings.newBookingAck
    ) {
      const { value } = await Swal.fire({
        title: 'COVID-19 ACKNOWLEDGMENT FORMS',
        showCancelButton: true,
        showConfirmButton: true,
        reverseButtons: true,
        text: 'Are you sure you want to turn on mandatory acknowledgment forms for all listings in the brokerage?',
        type: 'info',
      });
      if (!value) {
        setCovid19Settings({ ...covid19Settings, newBookingAck: false });
        return false;
      }
    }

    if (
      type === 'org' &&
      prevCovid19PrecautionsBtn === false &&
      covid19Settings.covid19PrecautionsBtn
    ) {
      const { value } = await Swal.fire({
        title: 'COVID-19 PRECAUTION BUTTON WARNING',
        showCancelButton: true,
        showConfirmButton: true,
        reverseButtons: true,
        text: 'Are you sure you want to display a COVID-19 precaution button when agents receive a confirmation email?',
        type: 'info',
      });
      if (!value) {
        setCovid19Settings({
          ...covid19Settings,
          covid19PrecautionsBtn: false,
        });
        return false;
      }
    }

    return true;
  };

  const handleSaveCovid19 = async (field: string, value: any) => {
    const newSettings = {
      ...covid19Settings,
      [field]: value,
    };
    setCovid19Settings(newSettings);
    setShouldSubmit(true);
    storeTemplateForRestore(newSettings);
  };

  useEffect(() => {
    const submitToServer = async () => {
      const toggleCheck = await checkToggleModals();
      if (toggleCheck) await saveCovid19OrgSettings(covid19Settings);
      setShouldSubmit(false);
    };
    if (shouldSubmit) submitToServer();
  }, [shouldSubmit]);

  const formSavedSwal = () =>
    Swal.fire({
      title: 'Form Updated',
      text: 'Your changes have been saved',
      type: 'success',
      confirmButtonText: 'Done',
    });

  return (
    <>
      <COVID19Settings
        handleToggle={handleSaveCovid19}
        handleEditAckTemplate={() => {
          setShowAckTemplateModal({ show: true, preview: false });
        }}
        handleEditPopupTemplate={() => {
          setShowPopupTemplateModal({ show: true, preview: false });
        }}
        settings={covid19Settings}
      />
      <COVID19TemplateModal
        title={'COVID-19 Waiver Template'}
        show={showAckTemplateModal.show}
        preview={showAckTemplateModal.preview}
        Wrapper={COVID19AckTemplateContainer}
        handleSave={() => {
          handleSaveCovid19('ackTemplate', covid19Settings.ackTemplate);
          setShowAckTemplateModal({ show: false, preview: false });
          formSavedSwal();
        }}
        onCancel={() => {
          restoreTemplate('ackTemplate');
          setShowAckTemplateModal({ show: false, preview: false });
        }}
        template={covid19Settings.ackTemplate}
        handleTemplateChange={newValue => {
          setCovid19Settings({ ...covid19Settings, ackTemplate: newValue });
        }}
      />
      <COVID19TemplateModal
        title={'MLS Popup Template'}
        Wrapper={React.Fragment}
        show={showPopupTemplateModal.show}
        preview={showPopupTemplateModal.preview}
        handleSave={() => {
          handleSaveCovid19('popupTemplate', covid19Settings.popupTemplate);
          setShowPopupTemplateModal({ show: false, preview: false });
          formSavedSwal();
        }}
        onCancel={() => {
          restoreTemplate('popupTemplate');
          setShowPopupTemplateModal({ show: false, preview: false });
        }}
        template={covid19Settings.popupTemplate}
        handleTemplateChange={(newValue: string) => {
          setCovid19Settings({ ...covid19Settings, popupTemplate: newValue });
        }}
      />
    </>
  );
};

export default COVID19SettingsContainer;
